<script setup>
import {Head, router, usePage} from '@inertiajs/vue3';
import {computed} from "vue";
import Clock from "@/Components/Icons/Clock.vue";
import {NButton, NIcon} from "naive-ui";

defineProps({
    canLogin: {
        type: Boolean,
    },
    canRegister: {
        type: Boolean,
    },
    laravelVersion: {
        type: String,
        required: true,
    },
    phpVersion: {
        type: String,
        required: true,
    },
});

function handleImageError() {
    document.getElementById('screenshot-container')?.classList.add('!hidden');
    document.getElementById('docs-card')?.classList.add('!row-span-1');
    document.getElementById('docs-card-content')?.classList.add('!flex-row');
}

const page = usePage();
const dashboardRoute = computed(() => {
    if (page.props.auth?.user && page.props.auth?.user.role === 'admin') {
        return route('admin.dashboard')
    } else if (page.props.auth?.user && page.props.auth?.user.role === 'customer') {
        return route('customer.home')
    } else {
        return '/'
    }
})
</script>

<template>
    <Head title="Welcome" />
    <div class="min-h-screen bg-gray-50 text-black/50 selection:bg-[#e2231a] selection:text-white dark:bg-black dark:text-white/50">
        <div class="relative flex min-h-screen flex-col items-center">
            <div class="relative w-full max-w-2xl px-3 lg:max-w-7xl">
                <header
                    class="mt-6 overflow-hidden rounded-2xl bg-white shadow-[0px_14px_34px_0px_rgba(0,0,0,0.08)] ring-1 ring-black/5 lg:mt-10 dark:bg-[#0e1430] dark:ring-white/10"
                >
                    <div class="grid items-center gap-8 p-8 lg:grid-cols-[1.1fr_1fr] lg:gap-12 lg:p-12">
                        <div>
                            <div class="flex items-center gap-3">
                                <img
                                    :src="`${page.props.appUrl}/storage/logo-mitchells.svg`"
                                    alt=""
                                    class="h-9 w-9"
                                />
                                <span class="text-xs font-bold uppercase tracking-[0.18em] text-[#e2231a]">
                                    Mitchells of Mansfield
                                </span>
                            </div>
                            <h1 class="mt-4 text-4xl font-extrabold tracking-tight text-[#16204a] lg:text-5xl dark:text-white">
                                Live Alerts
                            </h1>
                            <p class="mt-4 text-base/relaxed text-gray-600 dark:text-gray-300">
                                Real-time transport alerts between you and the transport team.
                            </p>
                            <nav v-if="canLogin" class="mt-8">
                                <div v-if="page.props.auth?.user" class="flex items-center gap-x-3">
                                    <n-button
                                        size="large" round strong
                                        color="#e2231a"
                                        @click="router.visit(dashboardRoute)"
                                    >
                                        <template #icon>
                                            <n-icon>
                                                <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 24 24"><path d="M8 4c-4.42 0-8 3.58-8 8s3.58 8 8 8s8-3.58 8-8s-3.58-8-8-8zm3.11 11.11a.99.99 0 0 1-1.39 0l-2.14-2.14c-.37-.37-.58-.88-.58-1.41V9c0-.57.48-1.04 1.07-1c.53.04.93.5.93 1.03v2.58l2.11 2.11a.99.99 0 0 1 0 1.39zm9.79 1.79c-.38-.38-.4-.91-.11-1.3c.76-1 1.2-2.25 1.2-3.6s-.45-2.6-1.2-3.6c-.3-.39-.27-.93.11-1.3a.989.989 0 0 1 1.48.09C23.4 8.52 24 10.19 24 12s-.6 3.48-1.62 4.82c-.36.47-1.06.5-1.48.08zm-2.52-2.52c-.36-.36-.37-.83-.16-1.23c.18-.34.28-.74.28-1.15s-.1-.81-.28-1.15c-.21-.4-.18-.89.14-1.21c.49-.49 1.26-.38 1.58.19c.36.64.56 1.39.56 2.17s-.2 1.53-.56 2.17c-.31.57-1.1.67-1.56.21z" fill="currentColor"></path></svg>
                                            </n-icon>
                                        </template>
                                        Dashboard
                                    </n-button>
                                </div>

                                <div v-else class="flex items-center gap-x-3">
                                    <n-button
                                        size="large" round strong
                                        color="#e2231a"
                                        @click="router.visit(route('login'))"
                                    >
                                        <template #icon>
                                            <n-icon>
                                                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 512 512" enable-background="new 0 0 512 512" xml:space="preserve"><g><path d="M256,48c-42.9,0-84.2,13-119.2,37.5c-34.2,24-60.2,57.2-75.1,96.1L58,192h45.7l1.9-5c8.2-17.8,19.4-33.9,33.5-48 c31.2-31.2,72.7-48.4,116.9-48.4c44.2,0,85.7,17.2,116.9,48.4c31.2,31.2,48.4,72.7,48.4,116.9c0,44.1-17.2,85.7-48.4,116.9 c-31.2,31.2-72.7,48.4-116.9,48.4c-44.1,0-85.6-17.2-116.9-48.4c-14-14-25.3-30.1-33.5-47.9l-1.9-5H58l3.6,10.4 c14.9,38.9,40.9,72.1,75.1,96.1C171.8,451.1,213,464,256,464c114.7,0,208-93.3,208-208S370.7,48,256,48z"></path><g><g><path d="M48,277.4h189.7l-43.6,44.7L224,352l96-96l-96-96l-31,29.9l44.7,44.7H48V277.4z"></path></g></g></g></svg>
                                            </n-icon>
                                        </template>
                                        Login
                                    </n-button>
                                    <n-button
                                        v-if="canRegister"
                                        size="large" round ghost
                                        @click="router.visit(route('register'))"
                                    >
                                        <template #icon>
                                            <n-icon>
                                                <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 24 24"><path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4s-4 1.79-4 4s1.79 4 4 4zm-9-2V8c0-.55-.45-1-1-1s-1 .45-1 1v2H2c-.55 0-1 .45-1 1s.45 1 1 1h2v2c0 .55.45 1 1 1s1-.45 1-1v-2h2c.55 0 1-.45 1-1s-.45-1-1-1H6zm9 4c-2.67 0-8 1.34-8 4v1c0 .55.45 1 1 1h14c.55 0 1-.45 1-1v-1c0-2.66-5.33-4-8-4z" fill="currentColor"></path></svg>
                                            </n-icon>
                                        </template>
                                        Register
                                    </n-button>
                                </div>
                            </nav>
                        </div>

                        <img
                            :src="`${page.props.appUrl}/storage/mitchells-truck.jpg`"
                            alt="Mitchells of Mansfield truck"
                            fetchpriority="high"
                            class="aspect-[3/2] w-full rounded-xl object-cover shadow-2xl"
                        />
                    </div>
                </header>

                <main class="mt-6">
                    <div class="grid gap-6 lg:grid-cols-2 lg:gap-8">
                        <div
                            id="docs-card"
                            class="flex flex-col items-start gap-6 overflow-hidden rounded-lg bg-white p-6 shadow-[0px_14px_34px_0px_rgba(0,0,0,0.08)] ring-1 ring-black/5 md:row-span-3 lg:p-10 dark:bg-zinc-900 dark:ring-zinc-800"
                        >
                            <div id="screenshot-container" class="relative flex w-full flex-1 items-stretch">
                                <img
                                    src="/storage/DashboardScreenshot.png"
                                    alt="Karus Live Alerts screenshot"
                                    class="aspect-video h-full w-full flex-1 rounded-[10px] object-top object-cover drop-shadow-[0px_4px_34px_rgba(0,0,0,0.06)] dark:hidden"
                                    @error="handleImageError"
                                />
                                <img
                                    src="/storage/DashboardScreenshotDark.png"
                                    alt="Karus Live Alerts screenshot"
                                    class="hidden aspect-video h-full w-full flex-1 rounded-[10px] object-top object-cover drop-shadow-[0px_4px_34px_rgba(0,0,0,0.25)] dark:block"
                                    @error="handleImageError"
                                />
                                <div
                                    class="absolute -bottom-16 -left-16 h-40 w-[calc(100%+8rem)] bg-gradient-to-b from-transparent via-white to-white dark:via-zinc-900 dark:to-zinc-900"
                                ></div>
                            </div>

                            <div class="relative flex items-center gap-6 lg:items-end">
                                <div id="docs-card-content" class="flex items-start gap-6 lg:flex-col">
                                    <div
                                        class="flex size-12 shrink-0 items-center justify-center rounded-full bg-[#e2231a]/10 sm:size-16"
                                    >
                                        <Clock />
                                    </div>
                                    <div class="pt-3 sm:pt-5 lg:pt-0">
                                        <h2 class="text-xl font-semibold text-black dark:text-white">Live Alerts for Transport</h2>
                                        <p class="mt-4 text-sm/relaxed">
                                            Manage Customers, and be notified as soon as updates are made.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            class="flex items-start gap-4 rounded-lg bg-white p-6 shadow-[0px_14px_34px_0px_rgba(0,0,0,0.08)] ring-1 ring-black/5 lg:pb-10 dark:bg-zinc-900 dark:ring-zinc-800"
                        >
                            <div
                                class="flex size-12 shrink-0 items-center justify-center rounded-full bg-[#e2231a]/10 sm:size-16"
                            >
                                <svg
                                    class="size-5 sm:size-6"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <g fill="#e2231a">
                                        <path
                                            d="M24 8.25a.5.5 0 0 0-.5-.5H.5a.5.5 0 0 0-.5.5v12a2.5 2.5 0 0 0 2.5 2.5h19a2.5 2.5 0 0 0 2.5-2.5v-12Zm-7.765 5.868a1.221 1.221 0 0 1 0 2.264l-6.626 2.776A1.153 1.153 0 0 1 8 18.123v-5.746a1.151 1.151 0 0 1 1.609-1.035l6.626 2.776ZM19.564 1.677a.25.25 0 0 0-.177-.427H15.6a.106.106 0 0 0-.072.03l-4.54 4.543a.25.25 0 0 0 .177.427h3.783c.027 0 .054-.01.073-.03l4.543-4.543ZM22.071 1.318a.047.047 0 0 0-.045.013l-4.492 4.492a.249.249 0 0 0 .038.385.25.25 0 0 0 .14.042h5.784a.5.5 0 0 0 .5-.5v-2a2.5 2.5 0 0 0-1.925-2.432ZM13.014 1.677a.25.25 0 0 0-.178-.427H9.101a.106.106 0 0 0-.073.03l-4.54 4.543a.25.25 0 0 0 .177.427H8.4a.106.106 0 0 0 .073-.03l4.54-4.543ZM6.513 1.677a.25.25 0 0 0-.177-.427H2.5A2.5 2.5 0 0 0 0 3.75v2a.5.5 0 0 0 .5.5h1.4a.106.106 0 0 0 .073-.03l4.54-4.543Z"
                                        />
                                    </g>
                                </svg>
                            </div>

                            <div class="pt-3 sm:pt-5">
                                <h2 class="text-xl font-semibold text-black dark:text-white">Hazardous Freight</h2>

                                <p class="mt-4 text-sm/relaxed">
                                    Customers can be set-up for both Standard and Hazardous Freight, and Hazardous Freight is clearly highlighted on the Dashboard.
                                </p>
                            </div>
                        </div>

                        <div
                            class="flex items-start gap-4 rounded-lg bg-white p-6 shadow-[0px_14px_34px_0px_rgba(0,0,0,0.08)] ring-1 ring-black/5 lg:pb-10 dark:bg-zinc-900 dark:ring-zinc-800"
                        >
                            <div
                                class="flex size-12 shrink-0 items-center justify-center rounded-full bg-[#e2231a]/10 sm:size-16"
                            >
                                <svg
                                    class="size-5 sm:size-6"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <g fill="#e2231a">
                                        <path
                                            d="M8.75 4.5H5.5c-.69 0-1.25.56-1.25 1.25v4.75c0 .69.56 1.25 1.25 1.25h3.25c.69 0 1.25-.56 1.25-1.25V5.75c0-.69-.56-1.25-1.25-1.25Z"
                                        />
                                        <path
                                            d="M24 10a3 3 0 0 0-3-3h-2V2.5a2 2 0 0 0-2-2H2a2 2 0 0 0-2 2V20a3.5 3.5 0 0 0 3.5 3.5h17A3.5 3.5 0 0 0 24 20V10ZM3.5 21.5A1.5 1.5 0 0 1 2 20V3a.5.5 0 0 1 .5-.5h14a.5.5 0 0 1 .5.5v17c0 .295.037.588.11.874a.5.5 0 0 1-.484.625L3.5 21.5ZM22 20a1.5 1.5 0 1 1-3 0V9.5a.5.5 0 0 1 .5-.5H21a1 1 0 0 1 1 1v10Z"
                                        />
                                        <path
                                            d="M12.751 6.047h2a.75.75 0 0 1 .75.75v.5a.75.75 0 0 1-.75.75h-2A.75.75 0 0 1 12 7.3v-.5a.75.75 0 0 1 .751-.753ZM12.751 10.047h2a.75.75 0 0 1 .75.75v.5a.75.75 0 0 1-.75.75h-2A.75.75 0 0 1 12 11.3v-.5a.75.75 0 0 1 .751-.753ZM4.751 14.047h10a.75.75 0 0 1 .75.75v.5a.75.75 0 0 1-.75.75h-10A.75.75 0 0 1 4 15.3v-.5a.75.75 0 0 1 .751-.753ZM4.75 18.047h7.5a.75.75 0 0 1 .75.75v.5a.75.75 0 0 1-.75.75h-7.5A.75.75 0 0 1 4 19.3v-.5a.75.75 0 0 1 .75-.753Z"
                                        />
                                    </g>
                                </svg>
                            </div>

                            <div class="pt-3 sm:pt-5">
                                <h2 class="text-xl font-semibold text-black dark:text-white">Real-time Notifications</h2>
                                <p class="mt-4 text-sm/relaxed">
                                    Alerts will be displayed in the app as soon as new information is entered by Customers, or following any changes to existing information.  Notifications can also be accompanied by a sound being played so that even if you're not actively looking at the app, you and your team will always be up to date.
                                </p>
                            </div>
                        </div>

                        <div
                            class="flex items-start gap-4 rounded-lg bg-white p-6 shadow-[0px_14px_34px_0px_rgba(0,0,0,0.08)] ring-1 ring-black/5 lg:pb-10 dark:bg-zinc-900 dark:ring-zinc-800"
                        >
                            <div
                                class="flex size-12 shrink-0 items-center justify-center rounded-full bg-[#e2231a]/10 sm:size-16"
                            >
                                <svg
                                    class="size-5 sm:size-6"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <g fill="#e2231a">
                                        <path
                                            d="M16.597 12.635a.247.247 0 0 0-.08-.237 2.234 2.234 0 0 1-.769-1.68c.001-.195.03-.39.084-.578a.25.25 0 0 0-.09-.267 8.8 8.8 0 0 0-4.826-1.66.25.25 0 0 0-.268.181 2.5 2.5 0 0 1-2.4 1.824.045.045 0 0 0-.045.037 12.255 12.255 0 0 0-.093 3.86.251.251 0 0 0 .208.214c2.22.366 4.367 1.08 6.362 2.118a.252.252 0 0 0 .32-.079 10.09 10.09 0 0 0 1.597-3.733ZM13.616 17.968a.25.25 0 0 0-.063-.407A19.697 19.697 0 0 0 8.91 15.98a.25.25 0 0 0-.287.325c.151.455.334.898.548 1.328.437.827.981 1.594 1.619 2.28a.249.249 0 0 0 .32.044 29.13 29.13 0 0 0 2.506-1.99ZM6.303 14.105a.25.25 0 0 0 .265-.274 13.048 13.048 0 0 1 .205-4.045.062.062 0 0 0-.022-.07 2.5 2.5 0 0 1-.777-.982.25.25 0 0 0-.271-.149 11 11 0 0 0-5.6 2.815.255.255 0 0 0-.075.163c-.008.135-.02.27-.02.406.002.8.084 1.598.246 2.381a.25.25 0 0 0 .303.193 19.924 19.924 0 0 1 5.746-.438ZM9.228 20.914a.25.25 0 0 0 .1-.393 11.53 11.53 0 0 1-1.5-2.22 12.238 12.238 0 0 1-.91-2.465.248.248 0 0 0-.22-.187 18.876 18.876 0 0 0-5.69.33.249.249 0 0 0-.179.336c.838 2.142 2.272 4 4.132 5.353a.254.254 0 0 0 .15.048c1.41-.01 2.807-.282 4.117-.802ZM18.93 12.957l-.005-.008a.25.25 0 0 0-.268-.082 2.21 2.21 0 0 1-.41.081.25.25 0 0 0-.217.2c-.582 2.66-2.127 5.35-5.75 7.843a.248.248 0 0 0-.09.299.25.25 0 0 0 .065.091 28.703 28.703 0 0 0 2.662 2.12.246.246 0 0 0 .209.037c2.579-.701 4.85-2.242 6.456-4.378a.25.25 0 0 0 .048-.189 13.51 13.51 0 0 0-2.7-6.014ZM5.702 7.058a.254.254 0 0 0 .2-.165A2.488 2.488 0 0 1 7.98 5.245a.093.093 0 0 0 .078-.062 19.734 19.734 0 0 1 3.055-4.74.25.25 0 0 0-.21-.41 12.009 12.009 0 0 0-10.4 8.558.25.25 0 0 0 .373.281 12.912 12.912 0 0 1 4.826-1.814ZM10.773 22.052a.25.25 0 0 0-.28-.046c-.758.356-1.55.635-2.365.833a.25.25 0 0 0-.022.48c1.252.43 2.568.65 3.893.65.1 0 .2 0 .3-.008a.25.25 0 0 0 .147-.444c-.526-.424-1.1-.917-1.673-1.465ZM18.744 8.436a.249.249 0 0 0 .15.228 2.246 2.246 0 0 1 1.352 2.054c0 .337-.08.67-.23.972a.25.25 0 0 0 .042.28l.007.009a15.016 15.016 0 0 1 2.52 4.6.25.25 0 0 0 .37.132.25.25 0 0 0 .096-.114c.623-1.464.944-3.039.945-4.63a12.005 12.005 0 0 0-5.78-10.258.25.25 0 0 0-.373.274c.547 2.109.85 4.274.901 6.453ZM9.61 5.38a.25.25 0 0 0 .08.31c.34.24.616.561.8.935a.25.25 0 0 0 .3.127.631.631 0 0 1 .206-.034c2.054.078 4.036.772 5.69 1.991a.251.251 0 0 0 .267.024c.046-.024.093-.047.141-.067a.25.25 0 0 0 .151-.23A29.98 29.98 0 0 0 15.957.764a.25.25 0 0 0-.16-.164 11.924 11.924 0 0 0-2.21-.518.252.252 0 0 0-.215.076A22.456 22.456 0 0 0 9.61 5.38Z"
                                        />
                                    </g>
                                </svg>
                            </div>

                            <div class="pt-3 sm:pt-5">
                                <h2 class="text-xl font-semibold text-black dark:text-white">Mobile-friendly UI</h2>
                                <p class="mt-4 text-sm/relaxed">
                                    Responsive UI - Customers can use it wherever they are.</p>
                                <p class="mt-4 text-sm/relaxed">
                                   Designed by <a href="https://www.renturapp.com/" target="_blank" rel="noopener noreferrer" class="text-[#e2231a] underline dark:text-[#ff6f66]">RentUrApp</a>
                                </p>
                                <div class="flex w-full justify-end items-center mt-6">
                                <img :src="`${page.props.appUrl}/storage/logo-renturapp.png`" alt="RentUrApp Logo" class="w-2/4"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
                <footer class="flex flex-col items-center gap-3 py-16 text-center text-sm text-black/60 dark:text-white/70">
                    <img
                        :src="`${page.props.appUrl}/storage/LiveAlertsLogo.jpg`"
                        alt="Live Alerts"
                        class="w-40 rounded-lg"
                    />
                </footer>
            </div>
        </div>
    </div>
</template>
